import React, { useCallback, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import styled from 'styled-components'
import Dropzone from 'react-dropzone'
import {
  FileUploadOutlined,
  PhotoCameraOutlined,
  InsertDriveFileOutlined,
  Close,
} from '@mui/icons-material'

import useInject from '~/hooks/useInject'
import { MAX_FILE_SIZE_MB } from '~/utilities/constants'
import TicketIDInput from '~/components/traffic-tickets/steps/BasicDetails/TicketIDInput'

const NAVY = '#12275c'
const NAVY_SOFT = '#1b3a7a'
const BORDER = '#e4e9f2'
const MUTED = '#6b7a90'

const ACCEPTED_TYPES = 'image/*,application/pdf'
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1000000

const DropCard = styled.div`
  border: 2px dashed ${(props) => (props.$active ? props.theme.colors.primary : '#c3d0e3')};
  background: ${(props) => (props.$active ? '#f2f7ff' : props.theme.colors.white)};
  border-radius: 14px;
  padding: 32px 28px;
  text-align: center;
  outline: none;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease;
  ${(props) => props.theme.media.mobile`
    padding: 24px 18px;
  `};
`

const IconTile = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: ${NAVY};
  color: ${(props) => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
`

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #eef2f8;
  color: ${MUTED};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
`

const Heading = styled.h2`
  font-family: ${(props) => props.theme.fonts.avenirNextBold};
  color: ${NAVY_SOFT};
  font-size: 1.35rem;
  line-height: 1.3;
  margin: 0;
  ${(props) => props.theme.media.mobile`
    font-size: 1.15rem;
  `};
`

const Body = styled.p`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: ${MUTED};
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 10px 0 0;
`

const PrimaryButton = styled.button`
  -webkit-appearance: none;
  border: none;
  cursor: pointer;
  background: ${NAVY};
  color: ${(props) => props.theme.colors.white};
  font-family: ${(props) => props.theme.fonts.avenirNextDemi};
  font-size: 1rem;
  border-radius: 8px;
  padding: 14px 28px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const GhostButton = styled.button`
  -webkit-appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  color: ${NAVY_SOFT};
  font-family: ${(props) => props.theme.fonts.avenirNextDemi};
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
`

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 22px;
`

const Hint = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: #9aa7b8;
  font-size: 0.85rem;
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  width: 100%;
`

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 22px;
  font-family: ${(props) => props.theme.fonts.avenirNextDemi};
  font-size: 0.85rem;
  color: ${MUTED};
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${BORDER};
  }
`

const Preview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid ${BORDER};
  border-radius: 10px;
  padding: 12px;
  text-align: left;
  img {
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 8px;
  }
`

const PreviewName = styled.div`
  flex: 1;
  min-width: 0;
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.text.label};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const IconButton = styled.button`
  -webkit-appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  color: ${MUTED};
  display: flex;
  padding: 4px;
`

const ErrorText = styled.div`
  color: ${(props) => props.theme.colors.error};
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  font-size: 0.85rem;
  min-height: 20px;
  margin-top: 12px;
`

const TicketNumberBlock = styled.div`
  margin-top: 12px;
  text-align: left;
`

const HiddenInput = styled.input`
  display: none;
`

const mapStore = (store) => ({
  isMobile: store.ui.isMobile,
  manualTrafficTicket: store.trafficLookup.manualTrafficTicket,
  manualTrafficTicketError: store.trafficLookup.manualTrafficTicketError,
  submitManualTicket: store.trafficLookup.submitManualTicket,
  fetcherName: store.trafficLookup.fetcherName,
  isSubmittingManualTicket: store.trafficLookup.isSubmittingManualTicket,
})

const TicketUploadCard = ({ subtitle }) => {
  const {
    isMobile,
    manualTrafficTicket,
    manualTrafficTicketError,
    submitManualTicket,
    fetcherName,
    isSubmittingManualTicket,
  } = useInject(mapStore)

  const [fileError, setFileError] = useState('')
  const cameraInputRef = useRef(null)

  const attachFiles = useCallback(
    (files) => {
      const file = files && files[0]
      if (!file) return
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileError(`${file.name} is larger than ${MAX_FILE_SIZE_MB} MB.`)
        return
      }
      setFileError('')
      manualTrafficTicket.handleTicketImageDrop([file])
    },
    [manualTrafficTicket]
  )

  useEffect(() => {
    const onPaste = (event) => {
      const files = event.clipboardData && event.clipboardData.files
      if (files && files.length) {
        event.preventDefault()
        attachFiles(Array.from(files))
      }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [attachFiles])

  const image = manualTrafficTicket.persistedTicketImage
  const hasAttachment = !!(image && image.isImageAttached)
  const isPdf = hasAttachment && image.contentType === 'application/pdf'
  const itemWord = fetcherName.indexOf('CRIM') > -1 ? 'charge' : 'ticket'

  const removeAttachment = () => {
    manualTrafficTicket.changeKey('persistedTicketImage', null)
    setFileError('')
  }

  const defaultSubtitle = `Photo, PDF, or scan — up to ${MAX_FILE_SIZE_MB} MB. We'll review it and get back to you with your options.`

  return (
    <Dropzone
      multiple={false}
      noClick
      noKeyboard
      accept={ACCEPTED_TYPES}
      maxSize={MAX_FILE_SIZE_BYTES}
      onDrop={(accepted, rejected) => {
        if (rejected && rejected.length && !(accepted && accepted.length)) {
          setFileError(
            `That file can't be used. Send a photo, scan or PDF under ${MAX_FILE_SIZE_MB} MB.`
          )
          return
        }
        attachFiles(accepted)
      }}
    >
      {({ getRootProps, getInputProps, isDragActive, open }) => (
        <DropCard {...getRootProps()} $active={isDragActive}>
          <input {...getInputProps()} />
          {hasAttachment ? (
            <Preview>
              {isPdf ? (
                <IconCircle style={{ margin: 0 }}>
                  <InsertDriveFileOutlined />
                </IconCircle>
              ) : (
                <img src={image.base64EncodedImage} alt={`Your ${itemWord}`} />
              )}
              <PreviewName>{image.fileName || `Your ${itemWord}`}</PreviewName>
              <IconButton type="button" aria-label="Remove file" onClick={removeAttachment}>
                <Close fontSize="small" />
              </IconButton>
            </Preview>
          ) : (
            <>
              <IconTile>
                <FileUploadOutlined />
              </IconTile>
              <Heading>Click, drop or paste your {itemWord} here</Heading>
              <Body>{subtitle || defaultSubtitle}</Body>
              <ButtonRow>
                <PrimaryButton type="button" onClick={open}>
                  Choose file
                </PrimaryButton>
                {isMobile && (
                  <GhostButton type="button" onClick={() => cameraInputRef.current.click()}>
                    <PhotoCameraOutlined fontSize="small" />
                    Take a photo
                  </GhostButton>
                )}
              </ButtonRow>
              {!isMobile && <Hint>Paste a screenshot with Ctrl/Cmd + V</Hint>}
            </>
          )}

          <HiddenInput
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(event) => attachFiles(Array.from(event.target.files))}
          />

          <OrDivider>or enter your {itemWord} number</OrDivider>
          <TicketNumberBlock onClick={(event) => event.stopPropagation()}>
            <TicketIDInput ticket={manualTrafficTicket} autoFocus={false} />
          </TicketNumberBlock>

          <ErrorText>{fileError || manualTrafficTicketError}</ErrorText>

          <PrimaryButton
            type="button"
            onClick={submitManualTicket}
            disabled={
              isSubmittingManualTicket ||
              (!hasAttachment && !manualTrafficTicket.ticketID.length)
            }
            style={{ marginTop: 8 }}
          >
            {isSubmittingManualTicket ? 'Submitting…' : `Submit ${itemWord}`}
          </PrimaryButton>
        </DropCard>
      )}
    </Dropzone>
  )
}

export default observer(TicketUploadCard)
